import React, { type PropsWithChildren } from 'react';
import { graphql, useFragment } from 'react-relay';
import { fg } from '@atlassian/jira-feature-gating';
import type { ConditionalFormattingSidebarContainer_view$key } from '@atlassian/jira-relay/src/__generated__/ConditionalFormattingSidebarContainer_view.graphql';
import type { ConditionalFormattingSidebarContainer_project$key } from '@atlassian/jira-relay/src/__generated__/ConditionalFormattingSidebarContainer_project.graphql';
import { MaybeMemoConditionalFormattingEntryPointProvider } from '../../../controllers/entrypoint-context/ConditionalFormattingEntrypointProvider.tsx';

type Props = {
	project: ConditionalFormattingSidebarContainer_project$key | null;
	view: ConditionalFormattingSidebarContainer_view$key | null;
};

export const ConditionalFormattingSidebarContainerInner = ({
	project,
	children,
	view,
}: PropsWithChildren<Props>) => {
	const projectData = useFragment<ConditionalFormattingSidebarContainer_project$key>(
		graphql`
			fragment ConditionalFormattingSidebarContainer_project on JiraProject {
				key
			}
		`,
		project,
	);

	const viewData = useFragment<ConditionalFormattingSidebarContainer_view$key>(
		graphql`
			fragment ConditionalFormattingSidebarContainer_view on JiraSpreadsheetView
			@argumentDefinitions(first: { type: "Int!" }) {
				...ConditionalFormattingEntrypointProvider_view @arguments(first: $first)
			}
		`,
		view,
	);

	if (projectData?.key != null && view != null) {
		return (
			<MaybeMemoConditionalFormattingEntryPointProvider
				projectKey={projectData.key}
				view={viewData ?? null}
			>
				{children}
			</MaybeMemoConditionalFormattingEntryPointProvider>
		);
	}

	return children;
};

export const ConditionalFormattingSidebarContainer = ({
	isEnabled,
	...rest
}: PropsWithChildren<
	{
		isEnabled?: boolean;
		project: ConditionalFormattingSidebarContainer_project$key | null;
	} & Props
>) => {
	if (
		// eslint-disable-next-line jira/ff/no-preconditioning
		isEnabled &&
		fg('jira_list_conditional_formatting') &&
		fg('saved_list_view_technical_changes')
	) {
		return <ConditionalFormattingSidebarContainerInner {...rest} />;
	}
	return rest.children;
};
